/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import {
  Select,
  SelectProps,
  Size,
  Heading,
  Drawer,
  Paper,
  Flex,
  RadioButton,
  RadioGroup,
  Icon,
  Grid,
  Button,
  ButtonType,
  NavTabs,
} from '@vaisala-rockhopper/components';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { string } from 'yup';
import { ProbeProps, ProductConfiguration, TransmitterProps } from '../configuration/ConfigurationFunctions';
import './ConfiguratorSelectDrawer.scss';
import _ from 'lodash';

interface SelectValue {
  width: Size;
  prefixCls: string;
  autoFocus: boolean;
  optionLabelProp: string;
  showArrow: boolean;
}

export interface ConfiguratorSelectDrawerProps extends SelectProps<SelectValue> {
  variant?: 'light' | 'dark';
  isDrawerOpen: boolean;
  setIsDrawerOpen: (args0: boolean) => void;
  drawerData: Array<ProbeProps | TransmitterProps>;
  selectedProductSelections: ProductConfiguration;
  drawerType: string;
  onConfiguratorSelectChange(value: string, position: string): void;
}

const ConfiguratorSelectDrawer = ({ ...props }: ConfiguratorSelectDrawerProps) => {
  const { t } = useTranslation();

  const [selectedTabKey, setSelectedTabKey] = React.useState('1');
  const [value, setValue] = React.useState<string>('');
  const [drawerData, setDrawerData] = React.useState<Array<ProbeProps | TransmitterProps>>(props.drawerData ?? []);
  const [selectedData, setSelectedData] = React.useState<string>('');
  const [drawerType, setDrawerType] = React.useState<string>(props.drawerType ?? '');
  const [fixedProbes, setFixedProbes] = React.useState<Array<ProbeProps | TransmitterProps>>([]);
  const [handheldProbes, setHandheldProbes] = React.useState<Array<ProbeProps | TransmitterProps>>([]);

  const onChange = (event: any) => {
    setValue(event.target.value);
  };

  const onDrawerClose = () => {
    props.setIsDrawerOpen(false);
  };

  const changeSelected = (value: string) => {
    setValue(value);
    if (value === '') {
      props.onConfiguratorSelectChange("remove", drawerType);
    } else {
      props.onConfiguratorSelectChange(value, drawerType);
    }
    props.setIsDrawerOpen(false);
  };

  const checkLocation = (imgSource: string) => {
    const location = window.location.hostname
    const protocol = window.location.protocol
    const urlPattern = /^https?:\/\/\S+/i
    let url: URL;

    url = new URL(protocol + '//' + location + imgSource)

    switch (location) {
      case 'indigo-selector.vaisala.com':
        imgSource = protocol + '//indigo-selector.vaisala.com' + url.pathname;
        break;
      case 'd2w7vks24hsr8b.cloudfront.net':
        imgSource = protocol + '//d2w7vks24hsr8b.cloudfront.net' + url.pathname;
        break;
      case 'vaisaladev.prod.acquia-sites.com':
      case 'vaisala.prod.acquia-sites.com':
      case 'vaisala.com':
      case 'www.vaisala.com':
        imgSource = protocol + '//indigo-selector.vaisala.com' + url.pathname;
        break;
      default:
        break;
    }
    return imgSource
  }

  React.useEffect(() => {
    if (props.drawerData.length > 0) {
      if (props.selectedProductSelections) {
        const selected = props.selectedProductSelections[props.drawerType.replace(/_/g, '')];
        const correctData = props.drawerData?.find((obj) => obj.value === selected.selected);
        if (correctData) {
          setValue(correctData.value);
          if (correctData.probe_type?.fixed === true) {
            setSelectedTabKey('2');
          } else {
            setSelectedTabKey('1');
          }
        }
      }
      if (props.drawerType !== "transmitter") {
        let drawerDataCopy = [...props.drawerData];
        let onlyHandHeldProbes: Array<ProbeProps | TransmitterProps> = []
        let onlyFixedProbes: Array<ProbeProps | TransmitterProps> = [];
        drawerDataCopy.forEach((probe) => {
          if (probe.probe_type?.fixed === true) {
            onlyFixedProbes.push(probe)
          } else {
            onlyHandHeldProbes.push(probe)
          }
        })
        setFixedProbes(onlyFixedProbes)
        setHandheldProbes(onlyHandHeldProbes)
      } else {
        setFixedProbes([])
        setHandheldProbes([])
      }
      setDrawerData(props.drawerData);
    }
  }, [props.drawerData, props.selectedProductSelections, props.drawerType]);

  React.useEffect(() => {
    if (!_.isEmpty(props.drawerType)) {
      setDrawerType(props.drawerType.replace(/_/g, ''));
    }
  }, [props.drawerType]);

  const openLink = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <Drawer className="select-drawer" isOpen={props.isDrawerOpen} anchor={'Right'}>
      <Heading level="3" onClick={() => onDrawerClose()}>
        <Icon name={VaiIcon.ChevronLeft} size={Size.XL} /> Select {t(`ui.${props.drawerType}`)}
      </Heading>
      <RadioGroup onChange={onChange} value={value}>
        {handheldProbes.length > 0 && (
          <NavTabs
            htmlId={['ProbeTabs']}
            selectedTabKey={selectedTabKey}
            setSelectedTabKey={setSelectedTabKey}
            onSelectedTabChanged={(selectedTabKey) => setSelectedTabKey(selectedTabKey)}
          >
            <NavTabs.Tab tabKey="1" title="Handheld">
              {handheldProbes.map((data, i) => {
                return (
                  <Paper
                    key={data.value}
                    onClick={() => changeSelected(data.value)}
                    className={`drawer-component-wrapper ${data.value === value ? 'selected-component' : ''}`}
                  >
                    <RadioButton key={data.value} value={data.value}>
                      {/* {drawerType === "transmitter" ? typeof data.label !== 'string' ? data.label[i18next.language] : data.label : data.label} */}
                      {data.infobox[i18next.language].title}
                      {/* <Icon onClick={() => openLink(data.infobox[i18next.language].link)} name={VaiIcon.LinkNewWindow} size={Size.XL} /> */}
                      <Button
                        onClick={() => openLink(data.infobox[i18next.language].link)}
                        buttonType={ButtonType.Link}
                        color={'aqua-medium'}
                        startIcon={<Icon name={VaiIcon.LinkNewWindow} />}
                      ></Button>
                    </RadioButton>
                    <Grid>
                      <img
                        className="drawer-data-img"
                        src={data.images.thumbnail ? checkLocation(data.images.thumbnail) : ''}
                        alt={data.value}
                        width="30"
                        height="75"
                      />
                      {/* <HMP1 width="30" height="75" /> */}
                      <div>
                        <p>{data.infobox[i18next.language].description}</p>
                        <ul>
                          {data.infobox[i18next.language].bulletpoints.map((val: string, i: number) => {
                            return <li key={val + '-' + i}>{val}</li>;
                          })}
                        </ul>
                      </div>
                      {/* </BodyText> */}
                    </Grid>
                  </Paper>
                );
              })}
            </NavTabs.Tab>
            <NavTabs.Tab tabKey="2" title="Fixed">
            {fixedProbes.map((data, i) => {
                return (
                  <Paper
                    key={data.value}
                    onClick={() => changeSelected(data.value)}
                    className={`drawer-component-wrapper ${data.value === value ? 'selected-component' : ''}`}
                  >
                    <RadioButton key={data.value} value={data.value}>
                      {/* {drawerType === "transmitter" ? typeof data.label !== 'string' ? data.label[i18next.language] : data.label : data.label} */}
                      {data.infobox[i18next.language].title}
                      {/* <Icon onClick={() => openLink(data.infobox[i18next.language].link)} name={VaiIcon.LinkNewWindow} size={Size.XL} /> */}
                      <Button
                        onClick={() => openLink(data.infobox[i18next.language].link)}
                        buttonType={ButtonType.Link}
                        color={'aqua-medium'}
                        startIcon={<Icon name={VaiIcon.LinkNewWindow} />}
                      ></Button>
                    </RadioButton>
                    <Grid>
                      <img
                        className="drawer-data-img"
                        src={data.images.thumbnail ? checkLocation(data.images.thumbnail) : ''}
                        alt={data.value}
                        width="30"
                        height="75"
                      />
                      {/* <HMP1 width="30" height="75" /> */}
                      <div>
                        <p>{data.infobox[i18next.language].description}</p>
                        <ul>
                          {data.infobox[i18next.language].bulletpoints.map((val: string, i: number) => {
                            return <li key={val + '-' + i}>{val}</li>;
                          })}
                        </ul>
                      </div>
                      {/* </BodyText> */}
                    </Grid>
                  </Paper>
                );
              })}
            </NavTabs.Tab>
          </NavTabs>
        )}
        {handheldProbes.length === 0 && drawerData.length > 0 &&
          drawerData.map((data, i) => {
            return (
              <Paper
                key={data.value}
                onClick={() => changeSelected(data.value)}
                className={`drawer-component-wrapper ${data.value === value ? 'selected-component' : ''}`}
              >
                <RadioButton key={data.value} value={data.value}>
                  {/* {drawerType === "transmitter" ? typeof data.label !== 'string' ? data.label[i18next.language] : data.label : data.label} */}
                  {data.infobox[i18next.language].title}
                  {/* <Icon onClick={() => openLink(data.infobox[i18next.language].link)} name={VaiIcon.LinkNewWindow} size={Size.XL} /> */}
                  <Button
                    onClick={() => openLink(data.infobox[i18next.language].link)}
                    buttonType={ButtonType.Link}
                    color={'aqua-medium'}
                    startIcon={<Icon name={VaiIcon.LinkNewWindow} />}
                  ></Button>
                </RadioButton>
                <Grid>
                  <img
                    className="drawer-data-img"
                    src={data.images.thumbnail ? checkLocation(data.images.thumbnail) : ''}
                    alt={data.value}
                    width="30"
                    height="75"
                  />
                  {/* <HMP1 width="30" height="75" /> */}
                  <div>
                    <p>{data.infobox[i18next.language].description}</p>
                    <ul>
                      {data.infobox[i18next.language].bulletpoints.map((val: string, i: number) => {
                        return <li key={val + '-' + i}>{val}</li>;
                      })}
                    </ul>
                  </div>
                  {/* </BodyText> */}
                </Grid>
              </Paper>
            );
          })
        }
      </RadioGroup>
    </Drawer>
  );
};

export default ConfiguratorSelectDrawer;
